import { ReactNode } from 'react';
import { Box, ButtonBase, Skeleton, Typography } from '@mui/material';
import { useMaterialUIController } from 'context';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';

export const useDashboardColors = () => {
    const [controller] = useMaterialUIController();
    return getCrmColors(controller.darkMode);
};

interface PanelProps {
    title: string;
    subtitle?: string;
    /// Alternância ou botão no canto direito do cabeçalho.
    action?: ReactNode;
    /// Números de apoio logo abaixo do título, antes do gráfico.
    stats?: ReactNode;
    footer?: string;
    children: ReactNode;
}

/// Moldura de todo bloco do painel (título, explicação curta, conteúdo), igual em todos.
export const Panel = ({ title, subtitle, action, stats, footer, children }: PanelProps) => {
    const cc = useDashboardColors();

    return (
        <Box
            sx={{
                background: cc.cardBg,
                border: `1px solid ${cc.border}`,
                borderRadius: 3,
                p: 2.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                minWidth: 0,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ color: cc.textPrimary, fontWeight: 700, fontSize: '0.95rem' }}>
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography variant="caption" sx={{ color: cc.textSecondary, display: 'block' }}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                {action}
            </Box>

            {stats && <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', minWidth: 0 }}>{stats}</Box>}

            <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>

            {footer && (
                <Typography variant="caption" sx={{ color: cc.textMuted, fontSize: '11px' }}>
                    {footer}
                </Typography>
            )}
        </Box>
    );
};

/// Um número de apoio no cabeçalho do bloco ("Perdidos 12").
export const PanelStat = ({ label, value, alert }: { label: string; value: ReactNode; alert?: boolean }) => {
    const cc = useDashboardColors();

    return (
        <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
            <Typography variant="caption" sx={{ color: cc.textSecondary, fontSize: '11px', display: 'block' }}>
                {label}
            </Typography>
            {/* Uma linha só: nome de etapa comprido empurraria o gráfico; o inteiro fica na dica. */}
            <Typography
                title={typeof value === 'string' ? value : undefined}
                sx={{
                    color: alert ? cc.eventDanger : cc.textPrimary,
                    fontWeight: 700,
                    fontSize: '1.15rem',
                    fontVariantNumeric: 'tabular-nums',
                    lineHeight: 1.3,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                {/* O "!" acompanha a cor: o alerta não depende só dela. */}
                {alert ? '! ' : ''}
                {value}
            </Typography>
        </Box>
    );
};

interface SegmentedProps<T extends string> {
    value: T;
    options: { value: T; label: string }[];
    onChange: (value: T) => void;
    /// Altura fixa, para alinhar com os selects ao lado. Sem ela, a do texto.
    height?: number;
}

/// Alternância de visão dentro de um bloco ("Total | Por origem").
export function Segmented<T extends string>({ value, options, onChange, height }: SegmentedProps<T>) {
    const cc = useDashboardColors();

    return (
        <Box
            role="group"
            sx={{
                display: 'flex',
                p: 0.4,
                gap: 0.4,
                borderRadius: '9px',
                background: cc.countBg,
                flexShrink: 0,
                boxSizing: 'border-box',
                ...(height && { height, border: `1px solid ${cc.border}` }),
            }}
        >
            {options.map((option) => {
                const selected = option.value === value;

                return (
                    <ButtonBase
                        key={option.value}
                        aria-pressed={selected}
                        onClick={() => onChange(option.value)}
                        sx={{
                            px: 1.4,
                            py: 0.6,
                            borderRadius: '7px',
                            fontSize: '0.78rem',
                            fontWeight: selected ? 700 : 500,
                            color: selected ? cc.textPrimary : cc.textSecondary,
                            background: selected ? cc.accentSoftBg : 'transparent',
                        }}
                    >
                        {option.label}
                    </ButtonBase>
                );
            })}
        </Box>
    );
}

/// Carregando ou vazio: o bloco guarda o lugar em vez de pular a tela.
export const PanelState = ({ loading, empty, height = 180 }: { loading: boolean; empty: string; height?: number }) => {
    const cc = useDashboardColors();

    if (loading) return <Skeleton variant="rounded" height={height} />;

    return (
        <Typography
            variant="caption"
            sx={{ color: cc.textMuted, py: 6, display: 'block', textAlign: 'center' }}
        >
            {empty}
        </Typography>
    );
};

export const money = (value: number) =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

export const percent = (value: number) => `${value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
